import { ReactRenderer } from '@tiptap/react';
import type { MentionNodeAttrs } from '@tiptap/extension-mention';
import type { SuggestionKeyDownProps, SuggestionOptions, SuggestionProps } from '@tiptap/suggestion';
import tippy from 'tippy.js';
import type { Instance as TippyInstance } from 'tippy.js';
import MentionList from './MentionList';
import type { MentionListProps, MentionListRef } from './MentionList';

export type MentionUser = { id: string; label: string };

const MAX_SUGGESTIONS = 8;

type MentionSuggestion = Omit<SuggestionOptions<MentionUser, MentionNodeAttrs>, 'editor'>;

/**
 * The `@` mention configuration for the Mention extension. `users` is a getter so the editor can be
 * created once while the list behind it keeps changing.
 */
export function mentionSuggestion(users: () => MentionUser[]): MentionSuggestion {
  return {
    char: '@',
    items: ({ query }) => {
      const needle = query.toLowerCase();
      return users()
        .filter((user) => user.label.toLowerCase().includes(needle))
        .slice(0, MAX_SUGGESTIONS);
    },
    render: () => {
      let component: ReactRenderer<MentionListRef, MentionListProps> | null = null;
      let popup: TippyInstance[] | null = null;

      const rect = (props: SuggestionProps<MentionUser, MentionNodeAttrs>) =>
        (props.clientRect ?? (() => new DOMRect())) as () => DOMRect;

      // `MentionList` renders nothing without items, so leaving the popup up would put an empty
      // box under the caret for every query that matches nobody.
      const toggle = (hasItems: boolean) => {
        const instance = popup?.[0];
        if (!instance) {
          return;
        }
        if (hasItems) {
          instance.show();
        } else {
          instance.hide();
        }
      };

      return {
        onStart: (props) => {
          component = new ReactRenderer(MentionList, {
            props: props as unknown as MentionListProps,
            editor: props.editor,
          });
          if (!props.clientRect) {
            return;
          }
          popup = tippy('body', {
            getReferenceClientRect: rect(props),
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
          });
          toggle(props.items.length > 0);
        },
        onUpdate: (props) => {
          component?.updateProps(props);
          popup?.[0]?.setProps({ getReferenceClientRect: rect(props) });
          toggle(props.items.length > 0);
        },
        onKeyDown: (props: SuggestionKeyDownProps) => {
          if (props.event.key === 'Escape') {
            popup?.[0]?.hide();
            return true;
          }
          return component?.ref?.onKeyDown(props) ?? false;
        },
        onExit: () => {
          popup?.[0]?.destroy();
          component?.destroy();
          popup = null;
          component = null;
        },
      };
    },
  };
}
